import React, { useState, useRef, useEffect, useCallback } from "react";
import "./styles/FilterDropdown.css";

/**
 * FilterDropdown - Single-select filter control.
 *
 * Opens on click and is fully keyboard operable. The previous version
 * opened on :hover only, which meant it could not be used from the
 * keyboard and could not be opened at all on a touch device.
 *
 * @param {Object} props
 * @param {string} props.label - Text shown on the trigger
 * @param {Array} props.options - Array of { label, value } or array of strings
 * @param {string|number} props.value - Current selected value
 * @param {Function} props.onChange - Receives a { target: { value } } event
 * @param {string} props.align - 'left' (default) or 'right' menu alignment
 * @param {boolean} props.active - Force the "filter applied" styling on or off.
 *   Defaults to "a value is set". Pass false for controls that always hold a
 *   value and are not narrowing anything, such as a sort order.
 */
const FilterDropdown = ({
  label,
  options = [],
  value,
  onChange,
  align = "left",
  active,
}) => {
  const optionList = Array.isArray(options)
    ? options.map((opt) =>
        typeof opt === "string" ? { label: opt, value: opt } : opt,
      )
    : [];

  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);

  const selectedIndex = optionList.findIndex((opt) => opt.value === value);

  const close = useCallback((refocus) => {
    setOpen(false);
    setHighlighted(-1);
    if (refocus && triggerRef.current) triggerRef.current.focus();
  }, []);

  const openMenu = () => {
    setOpen(true);
    setHighlighted(selectedIndex >= 0 ? selectedIndex : 0);
  };

  const select = (optionValue) => {
    onChange({ target: { value: optionValue } });
    close(true);
  };

  useEffect(() => {
    if (!open) return undefined;

    const onDocumentPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        close(false);
      }
    };
    // Closing on scroll avoids the menu drifting away from its trigger,
    // since it is positioned against the trigger and not the viewport —
    // but this listener is on `window` with capture:true, so it also
    // catches the menu's own internal scroll when the option list is
    // long enough to need one. Without the contains() check, scrolling
    // through the list closed it after the first wheel tick instead of
    // letting you scroll it.
    const onScroll = (event) => {
      if (rootRef.current && event.target instanceof Node && rootRef.current.contains(event.target)) {
        return;
      }
      close(false);
    };

    document.addEventListener("mousedown", onDocumentPointerDown);
    document.addEventListener("touchstart", onDocumentPointerDown);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDocumentPointerDown);
      document.removeEventListener("touchstart", onDocumentPointerDown);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, close]);

  const handleKeyDown = (event) => {
    const { key } = event;

    if (!open) {
      if (key === "ArrowDown" || key === "Enter" || key === " ") {
        event.preventDefault();
        openMenu();
      }
      return;
    }

    if (key === "Escape") {
      event.preventDefault();
      close(true);
    } else if (key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((i) => (i + 1) % optionList.length);
    } else if (key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((i) => (i - 1 + optionList.length) % optionList.length);
    } else if (key === "Home") {
      event.preventDefault();
      setHighlighted(0);
    } else if (key === "End") {
      event.preventDefault();
      setHighlighted(optionList.length - 1);
    } else if (key === "Enter" || key === " ") {
      event.preventDefault();
      if (optionList[highlighted]) select(optionList[highlighted].value);
    } else if (key === "Tab") {
      close(false);
    }
  };

  // "Active" means this filter is narrowing the result set. An empty
  // string is the conventional "all" option, so it does not count.
  const isActive =
    active === undefined
      ? value !== undefined && value !== null && value !== ""
      : active;

  return (
    <div
      ref={rootRef}
      className={[
        "dsSelect",
        open ? "isOpen" : "",
        isActive ? "isActive" : "",
        align === "right" ? "alignRight" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        ref={triggerRef}
        type="button"
        className="dsSelectTrigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={handleKeyDown}
      >
        <span className="dsSelectLabel">{label}</span>
        <span className="dsSelectChevron" aria-hidden="true" />
      </button>

      {open && (
        <ul className="dsSelectMenu" role="listbox" tabIndex={-1}>
          {optionList.map((option, index) => {
            const selected = option.value === value;
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={selected}
                className={[
                  "dsSelectOption",
                  selected ? "isSelected" : "",
                  index === highlighted ? "isHighlighted" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onMouseEnter={() => setHighlighted(index)}
                onClick={() => select(option.value)}
              >
                <span className="dsSelectCheck" aria-hidden="true">
                  {selected ? "✓" : ""}
                </span>
                <span className="dsSelectOptionLabel">{option.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FilterDropdown;
